import { useMutation } from '@apollo/client';
import {
  BackgroundIcon,
  DeleteModal,
  Generator,
  SkeletonLoading,
  toast,
  getTranslatedText,
  AccessControl,
  CLIENT_PORTAL_IT_DOC_DEFAULT_ENTITY_NAME,
  COMMON_CONSTANTS,
  useProduct
} from '@nile/components';
import { RMMClient } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import ITDocsContext from '@nile/rmm/src/modules/common/contexts/ITDocsListContext';
import {
  DELETE_NON_MONITORED_ASSET,
  GET_NON_MONITORED_ASSET_CLASS,
  GET_ENTITY_META,
  UPDATE_ASSET,
  GET_SHARED_ASSET_CLASS
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import {
  ActionsIconBlock,
  ActionsPopoverBlock,
  EditIcon,
  NonMonitoredAssetDetailsParent
} from '@nile/rmm/src/modules/it-documentation/components/pages/non-monitored-asset-details/styles';
import AssetsAssociationContainer from '@nile/rmm/src/modules/it-documentation/components/sub-pages/assets-association-container';
import AssetInfoDetails from '@nile/rmm/src/modules/it-documentation/components/sub-pages/non-monitored-asset-info/asset-info-details';
import { SVGIcon } from '@superops/super-icons';
import {
  Popover,
  Button,
  Heading,
  TextField
} from '@superops/ui-design-system';
import gql from 'graphql-tag';
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Tooltip } from 'react-clone-tippy';
import { withRouter } from 'react-router-dom';
import { ContentWrapper } from 'sopslibrary';
import { AssetAttachmentContainer } from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-attachments/index';
import styled from 'styled-components';
import CSSGridLayout from './CSSGridLayout';
import { useFlags } from 'launchdarkly-react-client-sdk';

function NonMonitoredAssetDetails(props) {
  const {
    assetId,
    intl,
    isClientPortal,
    clientPortalMatch,
    entityName: entityNameProp,
    classId: classIdProp,
    match,
    isRunbookAction = false
  } = props;

  const entityName = isClientPortal
    ? clientPortalMatch?.params?.entityName
    : entityNameProp ?? match?.params?.entityName;

  const classId = isClientPortal
    ? clientPortalMatch?.params?.classId
    : classIdProp ?? match?.params?.classId;

  const flags = useFlags();
  const [layout, setLayout] = useState({});
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);

  const [assetDetails, setAssetDetails] = useState(null);

  const [assetDetailsLoading, setAssetDetailsLoading] = useState(true);

  const [associatedAssets, setAssociatedAssets] = useState({});

  const [entityMeta, setEntityMeta] = useState([]);

  const [assetClass, setAssetClass] = useState({});

  const tabContext = useContext(TicketContext);

  const [actionsPopover, setActionsPopover] = useState(false);

  const [assetNamePopoverActive, setAssetNamePopoverActive] = useState(false);
  const [assetName, setAssetName] = useState('');
  const [toggleDocumentShare, setToggleDocumentShare] = useState(false);
  const [toggleAttachmentsPopup, setToggleAttachmentsPopup] = useState(false);

  const toggleActionsPopover = useCallback(() => {
    setActionsPopover(!actionsPopover);
  });
  const providerValue = {
    toggleDocumentShare,
    setToggleDocumentShare,
    toggleAttachmentsPopup,
    setToggleAttachmentsPopup
  };
  const fetchEntityMeta = (variables) => {
    RMMClient.query({
      query: GET_ENTITY_META,
      fetchPolicy: 'network-only',
      ...variables
    }).then(({ data }) => {
      if (data.getEntityMeta) {
        setEntityMeta(data?.getEntityMeta);
      }
    });
  };

  const fetchAssetClass = (variables) => {
    RMMClient.query({
      query: GET_NON_MONITORED_ASSET_CLASS,
      fetchPolicy: 'network-only',
      ...variables
    }).then(({ data }) => {
      if (data.getNonMonitoredAssetClass) {
        setAssetClass(data?.getNonMonitoredAssetClass || null);
        const layout = JSON.parse(
          data?.getNonMonitoredAssetClass.formMeta
        ).layout.map((el) => {
          return {
            ...el,
            isDraggable: false,
            isResizable: false
          };
        });
        // Sort the layout fields based on the y and x coodination fields
        // For custom CSS grid layout render
        setLayout(sortBy(layout, ['y', 'x']));
      }
    });
  };

  useEffect(() => {
    if (entityName && classId && assetId) {
      fetchEntityMeta({ variables: { entityName } });
      fetchAssetClass({ variables: { classId } });
    }
  }, [assetId, classId, entityName]);

  useEffect(() => {
    if (!isEmpty(entityMeta) && !isEmpty(assetClass)) {
      getAsset();
    }
    if (
      isClientPortal &&
      entityName === CLIENT_PORTAL_IT_DOC_DEFAULT_ENTITY_NAME &&
      !isEmpty(assetClass)
    ) {
      getAsset();
    }
  }, [entityMeta, assetClass]);

  const getAssetVariables = () => {
    const variables = {
      assetId: {
        type: 'ID',
        required: true
      }
    };
    return variables;
  };

  const getAssetFields = () => {
    const customFields =
      entityMeta?.attributes
        ?.filter(
          (field) => field.dataType !== 'entity' && field.isCustom === true
        )
        .map((el) => el.name) || null;

    let fieldsToRequest = [];

    fieldsToRequest.push('assetId');
    fieldsToRequest.push('name');
    fieldsToRequest.push({
      site: ['id', 'name']
    });
    if (customFields) {
      fieldsToRequest.push(`customFields(
        fields: ${JSON.stringify(customFields)}
      )`);
    }
    if (isMSPProduct) {
      fieldsToRequest.push({
        client: ['accountId', 'name']
      });
    }
    return fieldsToRequest;
  };

  const getAsset = async (loadingRequired = true) => {
    if (loadingRequired) {
      setAssetDetailsLoading(true);
    }
    const generator = new Generator({
      operation: 'getNonMonitoredAsset',
      variables: getAssetVariables(),
      fields: getAssetFields()
    });

    const queryString = generator.queryBuilder();

    const response = await RMMClient.query({
      query: gql`
        ${queryString.query}
      `,
      variables: {
        assetId
      },
      fetchPolicy: 'network-only'
    });
    setAssetDetails(response?.data?.getNonMonitoredAsset || []);
    setAssetDetailsLoading(false);
  };

  const [deleteAsset] = useMutation(DELETE_NON_MONITORED_ASSET, {
    client: RMMClient,
    onCompleted: (res) => {
      toast('success', 'Non monitored asset deleted successfully');
      tabContext.closeTab();
    },
    onError: () => {
      // toast('error', 'Something went wrong');
    }
  });

  const deleteNonMonitoredAsset = () => {
    deleteAsset({
      variables: {
        assetId
      }
    });
  };

  const updateCustomFields = (updatedFields) => {
    const clonedAssetDetails = cloneDeep(assetDetails);
    clonedAssetDetails.customFields = updatedFields;
    setAssetDetails(clonedAssetDetails);
  };

  const actionsActivator = (
    <AccessControl
      roleToCheck="itDocumentation.delete"
      render={
        <Button
          id={'itnonMonAssetAction'}
          secondary
          onClick={() => toggleActionsPopover()}
          icon={<SVGIcon name="Menu" />}
        ></Button>
      }
    />
  );

  /**
   * Save asset name
   */
  const updateAssetName = () => {
    editAsset({
      variables: { assetId, input: { name: assetName.trim() }, isMSPProduct }
    });

    toggleAssetNamePopover();
  };

  /**
   * Toggle popover method
   */

  const toggleAssetNamePopover = () => {
    setAssetName(assetDetails.name);
    setAssetNamePopoverActive(!assetNamePopoverActive);
  };

  /**
   *
   * Setting update assets mutation
   * @param {object} UPDATE_ASSET
   * @public
   */

  const [editAsset] = useMutation(UPDATE_ASSET, {
    client: RMMClient,
    onCompleted(res) {
      if (res) {
        setAssetDetails({
          ...assetDetails,
          name: res?.updateAsset?.name
        });
        tabContext.updateTab({
          module: 'NON_MONITORED_ASSET_DETAILS',
          data: {
            assetId: res?.updateAsset?.assetId,
            subject: res?.updateAsset?.name
          }
        });
      }
    },
    onError() {
      // toast('error', 'Something went wrong');
    }
  });

  /**
   *Icon render method
   */

  const assetNameActivator = (
    <AccessControl
      roleToCheck="itDocumentation.edit"
      render={
        <div
          className="visibilityActions cp fx actr"
          onClick={toggleAssetNamePopover}
        >
          <SVGIcon name="Edit" />
        </div>
      }
    />
  );
  if (assetDetailsLoading) {
    return (
      <SkeletonLoading
        hideStepNav
        hideHeader={isClientPortal}
        hideSecondaryHeader
        showPlainCardView={!isClientPortal}
        hideHeaderSub
        showTableHeader={false}
        tableColumn={2}
      />
    );
  }
  /** Take the objects where there are two elements in a row based on 'y' property */
  const twoElementsRowLayoutIdsArr: string[] = [];
  layout?.filter((value, index) => {
    if (layout?.findIndex((y) => y.y === value.y) !== index) {
      twoElementsRowLayoutIdsArr.push(layout[index - 1]?.i);
      twoElementsRowLayoutIdsArr.push(value.i);
    }
  });
  return (
    <ITDocsContext.Provider value={providerValue}>
      <NonMonitoredAssetDetailsParent
        className={`${isClientPortal ? 'vh90' : 'full-height'}`}
      >
        {!isEmpty(assetClass) && !isEmpty(assetDetails) ? (
          <div className="flex-column full-height">
            {!isClientPortal && !isRunbookAction && (
              <div className="header-block flex-row flex-align-center visibilityContainer">
                <span className="mr-5">
                  <SVGIcon name="ItDocumentationIcon" size="xl"></SVGIcon>
                </span>
                <div className="flex-item-balloon flex-row flex-align-center">
                  <Heading element="h1">{assetDetails?.name}</Heading>
                  {!isClientPortal && !isRunbookAction && (
                    <div>
                      <Popover
                        active={assetNamePopoverActive}
                        activator={assetNameActivator}
                        onClose={() => {}}
                      >
                        <div className="w200 scroll p8">
                          <label>
                            <FormattedMessage id="asset.nonMonitoredAsset.assetName.header" />
                          </label>
                          <div className="mt10">
                            <TextField
                              id="assetName"
                              value={assetName}
                              name="name"
                              onChange={(val) => {
                                setAssetName(val);
                              }}
                              placeholder={getTranslatedText(
                                intl,
                                'asset.nonMonitoredAsset.assetName.pl'
                              )}
                              type="text"
                              error={isEmpty(assetDetails?.name)}
                            />
                          </div>

                          <div className="flex-row jend mt10">
                            <div className="mr12">
                              <Button
                                id={'itnonMonAssetCancel'}
                                secondary
                                onClick={toggleAssetNamePopover}
                              >
                                <FormattedMessage id="common.cancel" />
                              </Button>
                            </div>

                            <Button
                              id={'itnonMonAssetSave'}
                              disabled={isEmpty(assetDetails.name?.trim())}
                              primary
                              onClick={() => updateAssetName()}
                            >
                              <FormattedMessage id="common.save" />
                            </Button>
                          </div>
                        </div>
                      </Popover>
                    </div>
                  )}
                </div>
                {!isClientPortal && !isRunbookAction && (
                  <div className="flex actr">
                    <Popover
                      activator={actionsActivator}
                      active={actionsPopover}
                      onClose={() => {
                        toggleActionsPopover();
                      }}
                    >
                      <ActionsPopoverBlock>
                        <DeleteModal
                          description={
                            <FormattedMessage id="it.docs.details.deleteItem.modalDescription" />
                          }
                          handleDeleteConfirmation={deleteNonMonitoredAsset}
                          title={
                            <FormattedMessage id="it.docs.details.deleteItem.title" />
                          }
                          modalActivator={
                            <div className="capitalize p10 cp">
                              <div className="tab-name">
                                <FormattedMessage id="common.delete" />
                              </div>
                            </div>
                          }
                        />
                      </ActionsPopoverBlock>
                    </Popover>
                    <AccessControl
                      roleToCheck="fileAttachments.add"
                      render={
                        <div className="ml-10">
                          <Tooltip
                            title={getTranslatedText(
                              intl,
                              'asset.details.attach.files'
                            )}
                            position="bottom"
                          >
                            <Button
                              icon={<SVGIcon name="FileAttachment" />}
                              onClick={() => setToggleAttachmentsPopup(true)}
                            />
                          </Tooltip>
                        </div>
                      }
                    />
                    {flags?.shareItDocsToClients && (
                      <AccessControl
                        roleToCheck="itDocumentation.edit"
                        render={
                          <div className="ml-10">
                            <Tooltip
                              title={getTranslatedText(intl, 'common.share')}
                              position="bottom"
                            >
                              <Button
                                icon={<SVGIcon name="Share" />}
                                onClick={() => setToggleDocumentShare(true)}
                              />
                            </Tooltip>
                          </div>
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            <ContentWrapper
              className={`flex-row content-block ${
                isClientPortal ? 'pnone' : ''
              } ${isRunbookAction ? 'full-width full-height' : ''}`}
              bgcolor="var(--sops-lightpurple-to-black-bg)"
            >
              <div
                className={`br10  ${
                  isRunbookAction ? 'full-height full-width' : ''
                } ${
                  isClientPortal ? 'w100 p2040' : 'w60 p20'
                } white-to-grey-bg fhp scroll`}
              >
                <div className={`full-height fx wrap scroll acstart`}>
                  {layout?.map((obj) => {
                    return (
                      <CSSGridLayout
                        key={obj.i}
                        layoutId={obj.i}
                        sizeOfGrid={obj.w}
                        twoElementsRowLayoutIdsArr={twoElementsRowLayoutIdsArr}
                        xPosition={obj.x}
                      >
                        <AssetInfoDetails
                          isRunbookAction={props?.isRunbookAction}
                          fieldObj={obj}
                          assetClass={assetClass}
                          customFields={assetDetails.customFields}
                          assetName={assetDetails.name}
                          client={assetDetails?.client}
                          site={assetDetails?.site}
                          refetchAssetDetails={() => {
                            getAsset(false);
                          }}
                          updatedCustomFields={updateCustomFields}
                          isClientPortal={isClientPortal}
                          entityName={entityName}
                          assetId={assetId}
                        />
                      </CSSGridLayout>
                    );
                  })}

                  {isClientPortal && !isRunbookAction && (
                    <AccessControl
                      roleToCheck="fileAttachments.view"
                      render={
                        <div className="mt-40 fx col">
                          <AssetAttachmentContainer
                            assetId={assetId}
                            isClientPortal={isClientPortal}
                          />
                        </div>
                      }
                    />
                  )}
                </div>
              </div>
              {!isClientPortal && !isRunbookAction && (
                <div className="flex-item-balloon fhp scroll">
                  <AssetsAssociationContainer
                    showMonitoredAssets={true}
                    assetClassName={assetClass.name}
                    assetId={assetId}
                    clientId={assetDetails?.client?.accountId}
                    siteId={assetDetails?.site?.id}
                    clientName={assetDetails?.client?.name}
                    siteName={assetDetails?.site?.name}
                  />
                </div>
              )}
            </ContentWrapper>
          </div>
        ) : (
          <span />
        )}
      </NonMonitoredAssetDetailsParent>
    </ITDocsContext.Provider>
  );
}

export default injectIntl(withRouter(NonMonitoredAssetDetails));
